/**
 * @file Modal.js (CSP-COMPLIANT)
 * @description Manages the main GUI modal.
 * Inline handlers and inline styles fully removed.
 */

export class Modal {
    constructor(services) {
        this.contentManager = services.content;
        this.eventBus = services.eventBus;
        this.stateManager = services.state;

        this.dom = {
            modal: document.getElementById('gui-modal'),
            modalBody: document.getElementById('modal-body'),
            modalCloseBtn: document.getElementById('modal-close-btn'),
        };
    }

    init() {
        if (!this.dom.modal) return;

        this.dom.modalCloseBtn.addEventListener('click', () => this.dom.modal.close());

        this.dom.modal.addEventListener('click', (e) => {
            if (e.target === this.dom.modal) this.dom.modal.close();
        });

        this.dom.modal.addEventListener('close', () => {
            const currentPath = this.stateManager.get('path');
            if (currentPath && currentPath.length > 1) {
                this.stateManager.navigate([currentPath[0]]);
            }
        });

        this.eventBus.on('modal:open:project', ({ projectId }) => this.openProjectModal(projectId));
        this.eventBus.on('modal:open:blog', ({ postId }) => this.openBlogModal(postId));
        this.eventBus.on('modal:open:mail', ({ mailtoUrl }) => this.openMailtoModal(mailtoUrl));
        this.eventBus.on('modal:open:html', ({ html }) => this.openHtmlModal(html));

        this.dom.modalBody.addEventListener('click', (e) => {
            const link = e.target.closest('.modal-link');
            if (!link) return;
            e.preventDefault();

            const projectId = link.dataset.projectId;
            const postId = link.dataset.postId;

            if (projectId || postId) {
                this.dom.modal.addEventListener(
                    'close',
                    () => {
                        if (projectId) this.eventBus.emit('modal:open:project', { projectId });
                        else if (postId) this.eventBus.emit('modal:open:blog', { postId });
                    },
                    { once: true }
                );
                this.dom.modal.close();
            }
        });
    }

    openHtmlModal(html) {
        this.dom.modalBody.innerHTML = html;
        this.dom.modal.showModal();
    }

    async openProjectModal(projectId) {
        const project = await this.contentManager.getProject(projectId);

        if (!project) {
            this.dom.modalBody.innerHTML =
                `<h2 class="page-title">Error</h2><p>Could not load project details.</p>`;
            this.dom.modal.showModal();
            return;
        }

        const highlightsHtml =
            project.metadata?.highlights?.length > 0
                ? `<h4 class="subsection-title">Highlights</h4>
                   <ul class="project-highlights-list">
                     ${project.metadata.highlights.map(hl => `<li>${hl}</li>`).join('')}
                   </ul>`
                : '';

        const imgHtml = project.image
            ? `<img src="${project.image}" alt="${project.title}" class="hero-image">`
            : '';

        this.dom.modalBody.innerHTML = `
            <div class="project-modal-content">
                ${imgHtml}
                <div class="image-placeholder" hidden>[Image Upload Pending]</div>
                <h2 class="modal-title">${project.title}</h2>
                <div class="project-body-html">${project.body_html}</div>
                ${highlightsHtml}
                <div class="project-links">
                    ${project.links.github ? `<a href="${project.links.github}" class="cta-outline" target="_blank">View on GitHub</a>` : ''}
                    ${project.links.demo ? `<a href="${project.links.demo}" class="cta-outline" target="_blank">Live Demo</a>` : ''}
                </div>
            </div>
        `;

        this.handleImageErrors();
        this.dom.modal.showModal();
    }

    async openBlogModal(postId) {
        const post = await this.contentManager.getBlogPost(postId);

        if (!post) {
            this.dom.modalBody.innerHTML =
                `<h2 class="page-title">Error</h2><p>Could not load blog post.</p>`;
            this.dom.modal.showModal();
            return;
        }

        const imgHtml = post.image
            ? `<img src="${post.image}" alt="${post.title}" class="hero-image">`
            : '';

        this.dom.modalBody.innerHTML = `
            <div class="blog-modal-content">
                ${imgHtml}
                <div class="image-placeholder" hidden>[Image Upload Pending]</div>
                <h2 class="modal-title">${post.title}</h2>
                <div class="blog-post-stats">
                    <span>${post.date}</span>
                    ${post.read_time ? `<span>• ${post.read_time}</span>` : ''}
                </div>
                <div class="blog-body">${post.body_html || post.excerpt}</div>
                ${post.url ? `<a href="${post.url}" class="cta-outline">View Original</a>` : ''}
            </div>
        `;

        this.handleImageErrors();
        this.dom.modal.showModal();
    }

    async openMailtoModal(mailtoUrl) {
        const email = mailtoUrl.replace('mailto:', '');
        const subject = 'Portfolio Inquiry';

        this.dom.modalBody.innerHTML = `
            <div class="mailto-modal-content">
                <h2 class="modal-title">Secure Channel Primed</h2>
                <p class="mailto-email-text">You can reach me at <strong>${email}</strong>.</p>
                <div class="modal-button-group">
                    <a href="https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}" class="cta-outline" target="_blank">Open with Gmail</a>
                    <a href="https://outlook.live.com/owa/?path=/mail/action/compose&to=${email}&subject=${subject}" class="cta-outline" target="_blank">Open with Outlook</a>
                    <a href="${mailtoUrl}?subject=${subject}" class="cta-secondary">Use Default Client</a>
                </div>
            </div>
        `;

        this.dom.modal.showModal();

        this.dom.modalBody.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', () => {
                setTimeout(() => this.dom.modal.close(), 300);
            });
        });
    }

    handleImageErrors() {
        const images = this.dom.modalBody.querySelectorAll('img.hero-image');
        images.forEach(img => {
            img.addEventListener('error', () => {
                img.classList.add('is-hidden');
                const placeholder = img.nextElementSibling;
                if (placeholder && placeholder.classList.contains('image-placeholder')) {
                    placeholder.hidden = false;
                }
            }, { once: true });
        });
    }
}
